import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Stethoscope, ShieldCheck, LogOut } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { formatApiError } from "@/lib/api";
import { useT } from "@/i18n/I18nContext";
import LocaleThemeSwitch from "@/components/common/LocaleThemeSwitch";

/*
 * Forced first-login reset (R-008) + self-service change (R-009). Rendered
 * full-screen by <App> whenever user.must_change_password is true; also reachable
 * voluntarily. On success the returned user clears the gate and we route home.
 */
export default function ChangePassword() {
  const navigate = useNavigate();
  const { user, changePassword, logout } = useAuth();
  const t = useT();
  const forced = Boolean(user?.must_change_password);

  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  async function handleSubmit(e) {
    e.preventDefault();
    setError("");
    if (next !== confirm) {
      setError(t("New password and confirmation do not match."));
      return;
    }
    setLoading(true);
    try {
      const u = await changePassword({
        current_password: current,
        password: next,
        password_confirmation: confirm,
      });
      if (!u?.must_change_password) navigate("/", { replace: true });
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  }

  return (
    <div data-testid="change-password-page" className="min-h-screen flex items-center justify-center p-6 bg-[#FAFBFC]">
      <div className="absolute top-4 right-4">
        <LocaleThemeSwitch variant="header" />
      </div>

      <div className="w-full max-w-md">
        <div className="flex items-center gap-3 mb-8">
          <div className="w-10 h-10 rounded-lg bg-[#0B7E8C] flex items-center justify-center">
            <Stethoscope className="w-5 h-5 text-white" />
          </div>
          <div>
            <div className="font-semibold text-[#1F2937]">{t("Hospital at Home")}</div>
            <div className="text-[#6B7280] text-xs uppercase tracking-wider">{t("Care Beyond Walls")}</div>
          </div>
        </div>

        <div className="mb-6">
          <div className="text-[11px] tracking-[0.2em] uppercase text-[#0B7E8C] font-semibold mb-2 flex items-center gap-1.5">
            <ShieldCheck className="w-3.5 h-3.5" /> {t("Account security")}
          </div>
          <h1 className="text-2xl font-semibold text-[#1F2937]">
            {forced ? t("Set a new password") : t("Change your password")}
          </h1>
          <p className="text-sm text-[#6B7280] mt-1">
            {forced
              ? t("For your security, you must set a new password before continuing.")
              : t("Choose a strong password you don't use anywhere else.")}
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label htmlFor="current" className="text-sm text-[#1F2937]">{t("Current password")}</Label>
            <Input
              id="current"
              data-testid="cp-current-input"
              type="password"
              value={current}
              onChange={(e) => setCurrent(e.target.value)}
              className="mt-1.5 h-11 border-[#E5E7EB] focus-visible:ring-[#0B7E8C]"
              autoComplete="current-password"
              required
            />
          </div>
          <div>
            <Label htmlFor="next" className="text-sm text-[#1F2937]">{t("New password")}</Label>
            <Input
              id="next"
              data-testid="cp-new-input"
              type="password"
              value={next}
              onChange={(e) => setNext(e.target.value)}
              className="mt-1.5 h-11 border-[#E5E7EB] focus-visible:ring-[#0B7E8C]"
              autoComplete="new-password"
              required
            />
            <p className="mt-1 text-[11px] text-[#6B7280]">
              {t("At least 12 characters, with upper- and lower-case letters and a number.")}
            </p>
          </div>
          <div>
            <Label htmlFor="confirm" className="text-sm text-[#1F2937]">{t("Confirm new password")}</Label>
            <Input
              id="confirm"
              data-testid="cp-confirm-input"
              type="password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              className="mt-1.5 h-11 border-[#E5E7EB] focus-visible:ring-[#0B7E8C]"
              autoComplete="new-password"
              required
            />
          </div>

          {error && (
            <div data-testid="cp-error" className="text-sm bg-[#EF444412] border border-[#EF444433] text-[#B91C1C] rounded-lg px-3 py-2">
              {error}
            </div>
          )}

          <Button
            type="submit"
            disabled={loading}
            data-testid="cp-submit-button"
            className="w-full h-11 bg-[#0B7E8C] hover:bg-[#128C97] text-white font-medium"
          >
            {loading ? t("Saving…") : t("Update password")}
          </Button>
        </form>

        <button
          type="button"
          onClick={logout}
          data-testid="cp-logout"
          className="mt-5 inline-flex items-center gap-1.5 text-sm text-[#6B7280] hover:text-[#0B7E8C]"
        >
          <LogOut className="w-4 h-4" /> {t("Sign out")}
        </button>
      </div>
    </div>
  );
}
